import UserBubble from '../bubbleItem/UserBubble'
import AssistantBubble from '../bubbleItem/AssistantBubble'
import { LoadingBubble } from '../bubbleItem/LoadingBubble'
import AutoHeightTextArea from 'components/textarea/AutoHeightTextArea/AutoHeightTextArea'
import { Button } from 'components/button/Button/Button'
import styled from 'styled-components'
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import useLoadingStore from 'stores/useLoadingStore'
import { useConfirmDialog } from 'hooks/Dialog/useConfirmDialog'
import { CustomScrollContainer } from 'components/scrollbar/CustomScollBarContainer/CustomScrollBarContainer'
import Scrollbar from 'react-scrollbars-custom'
import { chatbotUtil } from 'utils/chatbot'
import { Util } from 'utils'
import { ReportConfig } from 'containers/Report/Config/ReportConfig'
import { getTranslation } from 'utils/translation'
import { ChatItemType, UserChatItemType } from 'types/chatbot/chatbot'
import { isAssistant, isDate, isUser } from 'utils/type-guard'
import { PromptList } from '../prompt/PromptList'

type ChatbotBodyProps = {
  chatList: ChatItemType[]
  placeholder?: string // textarea placeholder
  loadingText?: string // loading text가 있을 경우, 응답을 기다리는 동안 전역 Loading UI 표시
  height?: string // chatbot body의 전체 높이
  disabled?: boolean // 버튼 비활성화 여부
  activeClearHistory?: boolean // 채팅 기록 초기화 버튼 활성화 여부
  initialText?: string
  setChatList: Dispatch<SetStateAction<ChatItemType[]>>
  handleSendMessage: (message: string) => Promise<ChatItemType | undefined> // 사용자 입력 메시지를 전달하여 AI 응답 요청 후, 메시지를 리턴받음
  abortRequest: () => void // stop 버튼 클릭 시 실행될 abort 함수
}

export const ChatbotBody = ({
  loadingText,
  height = '100%',
  placeholder = getTranslation('message', 'INFO.ASSISTANT_GUIDE'),
  disabled = false,
  activeClearHistory = false,
  chatList,
  initialText = ReportConfig.common.AI_DEFAULT_CHAT.originalText,
  setChatList,
  handleSendMessage,
  abortRequest,
}: ChatbotBodyProps) => {
  const { startLoading, stopLoading } = useLoadingStore()
  const { openConfirmDialog } = useConfirmDialog()

  const scrollbarRef = useRef<Scrollbar>(null)

  // 전송 버튼 누른 후, 아직 응답이 돌아오지 않았다면 true
  const [isLoading, setIsLoading] = useState(false)
  // 사용자에게서 입력받은 메시지
  const [message, setMessage] = useState('')

  /**
   *
   * @param createdAt 새로운 채팅의 생성 시간
   * @param prevCreatedAt 이전 채팅의 생성 시간
   * @description 새로운 채팅의 생성 시간과 이전 채팅의 생성 시간이 다른지 확인하는 함수
   * @returns 다르면 true, 같으면 false
   */
  const checkIsDifferentDate = (createdAt: string, prevCreatedAt: string) => {
    const prevChatDate = new Date(prevCreatedAt)
    const newChatDate = new Date(createdAt)

    return prevChatDate.toDateString() !== newChatDate.toDateString()
  }

  /**
   * @description 챗봇 요청 실패 시, 다시 한 번 요청할 수 있도록 하는 함수
   * @param originalText 실패한 요청의 원본 텍스트
   */
  const reloadMessage = async (originalText: string) => {
    if (isLoading) return

    if (loadingText) startLoading(loadingText)
    setIsLoading(true)

    try {
      scrollbarRef.current?.scrollToBottom()

      // 기존 텍스트로 재요청
      const response = await handleSendMessage(chatbotUtil.convertReqMessage(originalText))

      // 받은 응답을 채팅 리스트에 추가
      if (response) {
        const newChatList = [...chatList].slice(0, -1)
        setChatList([...newChatList, response])
      }
    } catch (e) {
      if (e instanceof Error && e.message === 'AbortError') {
        console.error('API 호출이 취소되었습니다.')
      } else {
        console.error(e)
      }
    } finally {
      stopLoading()
      setIsLoading(false)
    }
  }

  /**
   * @description 사용자 입력 메시지를 전송하는 함수
   */
  const onSendMessage = async () => {
    if (!message || isLoading) return

    const tempMessage = message

    // 사용자 입력 메시지 초기화
    setMessage('')

    try {
      if (loadingText) startLoading(loadingText)
      setIsLoading(true)

      // 사용자 입력 메시지를 마크다운 형식으로 변환 (+ sanitize) - chatList에 추가하여 UI 상으로 채팅이 올라오는 것처럼 보이게 함
      const sanitizedMessage = await chatbotUtil.getSanitizedMessage(tempMessage)

      setChatList((prev) => {
        const tempList = [...prev]

        const newChat: UserChatItemType = {
          type: 'user',
          content: sanitizedMessage,
          originalText: tempMessage,
          createdAt: new Date().toISOString(),
        }

        const prevChat = prev[prev.length - 1]

        // 이전 채팅이 있고, 새로운 채팅의 생성 시간과 이전 채팅의 생성 시간이 다르면 날짜 채팅 추가
        if (prevChat) {
          if (checkIsDifferentDate(newChat.createdAt, prevChat.createdAt)) {
            tempList.push({ type: 'date', createdAt: newChat.createdAt })
          }
        }

        tempList.push(newChat)

        return tempList
      })

      // \n => <br> 태그로 변환
      // 큰따옴표를 이스케이프 처리하여 JSON 파싱 오류 방지
      const response = await handleSendMessage(chatbotUtil.convertReqMessage(tempMessage))

      if (response) {
        // 받은 응답을 채팅 리스트에 추가
        setChatList((prev) => {
          const tempList = [...prev]

          const prevChat = prev[prev.length - 1]

          // 이전 채팅이 있고, 새로운 채팅의 생성 시간과 이전 채팅의 생성 시간이 다르면 날짜 채팅 추가
          if (prevChat) {
            if (checkIsDifferentDate(response.createdAt, prevChat.createdAt)) {
              tempList.push({ type: 'date', createdAt: response.createdAt })
            }
          }

          tempList.push(response)

          return tempList
        })
      }
    } catch (e) {
      if (e instanceof Error && e.message === 'AbortError') {
        console.error('API 호출이 취소되었습니다.')
      } else {
        setChatList((prev) => [
          ...prev,
          {
            type: 'assistant',
            content: '응답 생성에 문제가 발생했습니다. 다시 시도해주세요.',
            originalText: '응답 생성에 문제가 발생했습니다. 다시 시도해주세요.',
            status: 'error',
            createdAt: new Date().toISOString(),
          },
        ])
      }
    } finally {
      stopLoading()
      setIsLoading(false)
    }
  }

  /**
   * @description 채팅 기록 초기화 버튼 클릭 시, 채팅 기록 초기화 확인 모달 창 열기
   */
  const openClearChatHistoryConfirmDialog = () => {
    openConfirmDialog({
      size: 'small',
      state: 'info',
      title: getTranslation('resource', 'CLEAR_HISTORY'),
      body: {
        content: getTranslation('message', 'CONFIRM.DELETE', ['HISTORY']),
      },
      button: {
        label: 'Clear',
        onClick: () => {
          setChatList([
            {
              type: 'date',
              createdAt: new Date().toISOString(),
            },
            {
              type: 'assistant',
              content: initialText,
              originalText: initialText,
              createdAt: new Date().toISOString(),
            },
          ])
        },
      },
    })
  }

  useEffect(() => {
    const container = scrollbarRef.current

    if (container) {
      container.scrollToBottom()
    }
  }, [chatList])

  return (
    <StyledChatbotBody $height={height}>
      <CustomScrollContainer ref={scrollbarRef} width="100%" height="100%">
        <main>
          {chatList.map((item, index) => {
            if (isUser(item))
              return (
                <UserBubble
                  key={index}
                  content={item.content}
                  originalText={item.originalText ?? ''}
                  createdAt={item.createdAt}
                  reloadable={
                    index === chatList.length - 2 && chatList[index + 1]?.status === 'error'
                  }
                  reloadMessage={reloadMessage}
                />
              )
            else if (isAssistant(item))
              return (
                <AssistantBubble
                  key={index}
                  content={item.content}
                  originalText={item.originalText ?? ''}
                  createdAt={item.createdAt}
                  status={item.status}
                />
              )
            else if (isDate(item))
              return (
                <p className="chat-date" key={index}>
                  {Util.formatDate(item.createdAt, false)}
                </p>
              )
            else return null
          })}
          {isLoading && <LoadingBubble />}
        </main>
      </CustomScrollContainer>
      {/* <PromptList /> */}
      <footer>
        <AutoHeightTextArea
          placeholder={placeholder}
          value={message}
          onValueChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMessage(e.target.value)}
          onKeyDown={async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
            }
          }}
          onKeyUp={async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              await onSendMessage()
            }
          }}
          maxHeight="10rem"
          style={{
            border: 0,
            backgroundColor: 'var(--bg-gray-normal)',
            font: 'var(--label-small-400)',
            padding: 0,
            minHeight: '5rem',
          }}
        />
        <div className="button-container">
          {activeClearHistory ? (
            <Button
              preIcon="Trash03"
              onClick={openClearChatHistoryConfirmDialog}
              size="small"
              color="strong"
              type="text"
              disabled={chatList.length === 1 || isLoading || disabled}
              tooltip={{
                direction: 'top-left',
                label: getTranslation('resource', 'CLEAR_HISTORY'),
              }}
            />
          ) : (
            <div />
          )}
          <Button
            type="solid"
            width="2rem"
            preIcon={isLoading ? 'Stop' : 'ArrowUp'}
            color="primary"
            size="small"
            disabled={(!message && !isLoading) || disabled}
            onClick={isLoading ? abortRequest : onSendMessage}
          />
        </div>
      </footer>
    </StyledChatbotBody>
  )
}

const StyledChatbotBody = styled.div<{ $height: string }>`
  box-sizing: border-box;
  width: 100%;
  height: ${({ $height }) => $height};
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  main {
    .chat-date {
      width: 100%;
      display: flex;
      justify-content: center;
      font: var(--label-small-400);
      font-size: 0.8rem;
      color: var(--text-gray-4);
      line-height: 1.2rem;
    }

    box-sizing: border-box;
    padding: 0.8rem;
    flex: 1 1 0%;
    display: flex;
    gap: 0.4rem;
    flex-direction: column;

    table {
      border: 0.1rem solid var(--bg-gray-2);

      th {
        padding: 0.8rem;
        background-color: var(--bg-gray-5);
        border-bottom: 0.1rem solid var(--bg-gray-2);
        font: var(--body-small-600);
        color: var(--text-gray-normal);
        text-align: left;
        &:not(:last-child) {
          border-right: 0.1rem solid var(--bg-gray-2);
        }
      }

      tr {
        &:nth-child(odd) {
          background-color: var(--bg-gray-normal);
        }
        &:nth-child(even) {
          background-color: var(--bg-gray-0);
        }
        td {
          box-sizing: border-box;
          padding: 0.8rem;
          color: var(--text-gray-6);
          border-bottom: 0.1rem solid var(--bg-gray-2);
          vertical-align: middle;
          &:not(:last-child) {
            border-right: 0.1rem solid var(--bg-gray-2);
          }
        }
      }
    }
  }

  footer {
    background-color: var(--bg-gray-normal);
    border-top: 1px solid var(--bg-gray-2);
    padding: 1.2rem 0.8rem 0.6rem 0.8rem;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    align-items: flex-end;

    .button-container {
      width: 100%;
      display: flex;
      justify-content: space-between;
    }
  }
`
